import { useQueryClient } from '@tanstack/react-query';
import { Formik, FormikErrors } from 'formik';

import { ContractMessage, ContractState } from '../../../types/ContractMessage';
import { CustomerCompanyMessage } from '../../../types/CustomerCompanyMessage';
import { useToasts } from '../../cdl/Toast/useToasts';
import { validateContract } from '../../common/api/clients/contracts.api';
import { queries } from '../../common/api/queryKeys/queries';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { ContractDrawerForm } from '../../contracts/form/ContractDrawerForm';
import {
    getInitialContractFormValues,
    mapBackendValidationErrors,
    toContractPayload,
    validateContractForm,
} from '../../contracts/form/contractForm.helpers';
import { ContractFormValues } from '../../contracts/form/contractForm.types';
import { useContractUpdate } from '../../contracts/form/hooks/useContractUpdate';

interface EditContractDrawerProps {
    contract: ContractMessage;

    onSuccess: () => void;
}

export const EditContractDrawer = ({ contract, onSuccess }: EditContractDrawerProps) => {
    const { addToast, addErrorToast } = useToasts();
    const queryClient = useQueryClient();
    const updateMutation = useContractUpdate();
    const role = useRole();
    const allCustomerIds = role.getCompanies().map((company) => company.id);
    const initialValues = getInitialContractFormValues(contract);
    const hasAllCustomerIdsSelected =
        !role.isSuperAdmin() &&
        allCustomerIds.length > 0 &&
        initialValues.customerIds.length === allCustomerIds.length &&
        allCustomerIds.every((customerId) => initialValues.customerIds.includes(customerId));

    const getCustomerIdsFromSelectedGroup = (customerGroupId: string): string[] => {
        if (!customerGroupId) {
            return [];
        }

        const query = queries.customers.list({ groupId: customerGroupId });
        const customers = queryClient.getQueryData<CustomerCompanyMessage[]>(query.queryKey) ?? [];
        return customers.map((company) => company.id);
    };

    const handleSubmit = async (values: ContractFormValues) => {
        const isAwardingDraft = contract.state === ContractState.DRAFT && values.state !== ContractState.DRAFT;
        let customerIds = values.customerIds;
        if (!customerIds.length) {
            if (role.isSuperAdmin()) {
                customerIds = getCustomerIdsFromSelectedGroup(values.customerGroupId);
            } else {
                customerIds = allCustomerIds;
            }
        }
        const payload = {
            ...toContractPayload({ ...values, customerIds }),
            priceListActivationMode: contract.priceListActivationMode,
        };

        try {
            await updateMutation.mutateAsync({
                id: contract.id,
                data: payload,
            });
            addToast(
                isAwardingDraft
                    ? translate('contracts.form.toast.publishSuccess')
                    : translate('contracts.form.toast.editSuccess')
            );
            onSuccess();
        } catch {
            addErrorToast(
                isAwardingDraft
                    ? translate('contracts.form.toast.publishError')
                    : translate('contracts.form.toast.editError')
            );
        }
    };

    const validate = async (values: ContractFormValues) => {
        const errors = validateContractForm(values);

        if (Object.keys(errors).length) {
            return errors;
        }

        let customerIds = values.customerIds;
        if (!customerIds.length) {
            if (role.isSuperAdmin()) {
                customerIds = getCustomerIdsFromSelectedGroup(values.customerGroupId);
            } else {
                customerIds = allCustomerIds;
            }
        }
        try {
            const validationPayload = {
                ...toContractPayload({ ...values, customerIds }),
                priceListActivationMode: contract.priceListActivationMode,
            };
            const validationResponse = await validateContract(validationPayload, contract.id);

            return mapBackendValidationErrors(validationResponse, values);
        } catch {
            addErrorToast(translate('contracts.form.toast.editError'));

            return { _validation: 'request_failed' } as FormikErrors<ContractFormValues>;
        }
    };

    return (
        <Formik
            initialValues={{
                ...initialValues,
                customerIds: hasAllCustomerIdsSelected ? [] : initialValues.customerIds,
            }}
            validate={validate}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            <ContractDrawerForm
                isSupplierReadOnly={!!contract.supplierId}
                showSaveDraftButton={contract.state === ContractState.DRAFT}
                showAwardButton={contract.state === ContractState.DRAFT}
            />
        </Formik>
    );
};
